import { Component, inject, ChangeDetectionStrategy } from '@angular/core';
import { ReactiveFormsModule, UntypedFormBuilder, UntypedFormGroup } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { first } from 'rxjs/operators';
import { MemberIdKeeperService } from '../../../core/member-id-keeper.service';
import { MemberService } from '../../../core/members/member.service';
import { SessionService } from '../../../core/session/session.service';
import { SettingsService } from '../../../core/settings/settings.service';
import { BaseParamsService } from '@app/core/base-params.service';
import { LoyaltyClubFooterComponent } from '@app/shared/components/loyalty-club-footer/loyalty-club-footer.component';
import { TranslatePipe } from '@ngx-translate/core';
import { MsisdnInputComponent } from '@app/shared/components/msisdn-input/msisdn-input.component';
import { AppFocusableDirective } from '@app/shared/directives/focusable.directive';

import { TopNavbarComponent } from '@app/shared/components/top-navbar/top-navbar.component';
import { AlertComponent } from '@app/shared/components/alert/alert.component';

@Component({
  selector: 'app-verification',
  templateUrl: './verification.component.html',
  styleUrl: 'verification.component.scss',
  changeDetection: ChangeDetectionStrategy.Eager,
  imports: [
    LoyaltyClubFooterComponent,
    TranslatePipe,
    MsisdnInputComponent,
    ReactiveFormsModule,
    AppFocusableDirective,
    TopNavbarComponent,
    AlertComponent,
  ],
})
export class VerificationComponent {
  private route = inject(ActivatedRoute);
  private memberService = inject(MemberService);
  private settingsService = inject(SettingsService);
  private sessionService = inject(SessionService);
  private router = inject(Router);
  private memberIdKeeperService = inject(MemberIdKeeperService);
  private baseParamsService = inject(BaseParamsService);


  public isEmailVerified = false;
  public isPhoneVerified = true;
  public form: UntypedFormGroup;
  public settings = this.settingsService.settings;
  public displayLogout = false;
  public displayStatus = false;
  public token: string | null = null;
  public verificationStatus: any;
  public isVerificationEmailSent = false;
  public isVerificationSMSSent = false;
  public isVerificationEmailError = false;
  public isVerificationSMSError = false;

  constructor() {
    const formBuilder = inject(UntypedFormBuilder);


    this.route.queryParamMap.pipe(
      first(),
    ).subscribe((params) => this.onParamsReceived(params.get('token')));

    this.form = formBuilder.group({
      email: '',
      msisdn: '',
    });

  }

  public signOut(): void {
    this.memberService.signOut()
      .subscribe(() => {
        this.displayStatus = true;
        this.displayLogout = false;
      });
  }

  public verifyMsisdn(): void {
    this.memberService.sendVerificationSms(this.form.get('msisdn')?.value, this.token)
      .subscribe(() => {
        this.isVerificationSMSSent = true;
        this.isVerificationSMSError = false;
      }, () => {
        this.isVerificationSMSError = true;
      });
  }

  public verifyEmail(): void {
    this.memberService.sendVerificationEmail(this.form.get('email')?.value, this.token)
      .subscribe(() => {
        this.isVerificationEmailSent = true;
        this.isVerificationEmailError = false;
      }, () => {
        this.isVerificationEmailError = true;
      });
  }


  private onParamsReceived(token: string | null): void {
    this.token = token;

    this.sessionService.isBasicallyAuthenticated()
      .subscribe((isAuthenticated) => {
        if (!isAuthenticated) {
          this.handleNotAuthenticated();
        } else { // Authenticated user
          if (!token) {
            // Clicked from pop-up
            this.handleAuthenticatedWithoutToken();
          } else {
            // Clicked from link while being already logged in.
            this.handleAuthenticatedWithToken();
          }
        }
      });
  }

  private handleNotAuthenticated(): void {
    this.memberService.getVerificationStatus(this.token)
      .subscribe(verificationStatus => {
        this.setVerificationStatus(verificationStatus);
        this.displayStatus = true;
      }, () => {
        this.redirectTo404();
      });
  }

  private handleAuthenticatedWithoutToken(): void {
    this.memberService.getVerificationStatus().subscribe(verificationStatus => {
      this.setVerificationStatus(verificationStatus);
      this.displayStatus = true;
    });
  }

  private handleAuthenticatedWithToken(): void {
    this.memberService.getVerificationStatus(this.token)
      .subscribe((verificationStatus: Record<string, unknown>) => {
        if (verificationStatus.id !== this.memberIdKeeperService.readMemberId()) {
          // Display logout button; don't show statuses yet.
          this.displayLogout = true;
        } else {
          this.displayStatus = true;
        }
        this.setVerificationStatus(verificationStatus);

      }, () => {
        this.redirectTo404();
      });
  }

  private setVerificationStatus(verificationStatus: any): void {
    this.form.get('email')?.patchValue(verificationStatus.email);
    this.form.get('msisdn')?.patchValue(verificationStatus.msisdn);
    this.isEmailVerified = ['verified', 'unsubscribed'].includes(verificationStatus.email_status);
    this.isPhoneVerified = verificationStatus.sms_status === 'verified';
    this.verificationStatus = verificationStatus;
  }

  private redirectTo404(): void {
    this.router.navigate([this.baseParamsService.getRootURI(), '404'], { skipLocationChange: true });
  }

}
